import { errorText } from '@/api/client';
/**
 * «Ring» and «Melding» in the person panel (owner 2026-10-28). Ring is a tel: link on a phone; on a PC,
 * which cannot call, the number itself is shown and copies on a press. Melding is
 * sent through Timefly (POST /admin/users/:id/message): a push and a row in the person's varsler, or
 * an e-mail when the person has no app. An anchored popover on a PC, a bottom sheet on a phone.
 */
import { sendMessage } from '@/api/data';
import { T } from '@/i18n';
import { Button, toast } from '@/ui';
import { fmtPhone } from '@/ui/format';
import { Icon } from '@/ui/icons';
import * as Dialog from '@radix-ui/react-dialog';
import * as Popover from '@radix-ui/react-popover';
import { useState } from 'react';
import css from './PersonPanel.module.css';
import { usePhone } from './Phone';

/** a phone or a touch screen: the device can call, so Ring is the link itself */
export const CALLS = '(max-width:560px), (pointer:coarse)';
const MAX = 500;

/** `quiet`: on a PC the number sits as quiet text under the person panel's contact row */
export function CallButton({ phone, name, quiet = false }: { phone: string; name: string; quiet?: boolean }) {
  const touch = usePhone(CALLS);
  const [copied, setCopied] = useState(false);
  if (touch)
    return (
      <a className="btn" href={`tel:${phone}`} data-testid="vperson-call">
        <Icon name="phone" />
        {T('Ring')}
      </a>
    );
  const copy = () =>
    navigator.clipboard?.writeText(phone).then(
      () => setCopied(true),
      () => toast(T('Kunne ikke kopiere. Merk nummeret og kopier det selv.')),
    );
  // a PC cannot call: the number itself is the button and copies it (owner 2026-10-28, option 2)
  return (
    <button
      type="button"
      className={quiet ? css.qcall : 'btn'}
      data-testid="vperson-call"
      onClick={copy}
      onBlur={() => setCopied(false)}
      aria-label={T('Kopier nummeret til {name}: {phone}', { name, phone })}
      title={T('Kopier nummer')}
    >
      <Icon name={copied ? 'check' : 'copy'} />
      {/* written as people write it, «+47 934 56 789»; the copy is the plain number (#272 A-24) */}
      {copied ? T('Kopiert') : fmtPhone(phone)}
    </button>
  );
}

export function MessageButton({ id, name }: { id: number; name: string }) {
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const change = (o: boolean) => {
    setOpen(o);
    if (!o) setError('');
  };
  const send = async () => {
    setBusy(true);
    setError('');
    try {
      const { delivered } = await sendMessage(id, text.trim());
      setOpen(false);
      setText('');
      toast(
        delivered === 'push'
          ? T('Sendt til {name}. {name} får varsel på mobilen.', { name })
          : delivered === 'email'
            ? T('Sendt til {name} som e-post.', { name })
            : T('Sendt til {name}. Meldingen ligger i varslene i Timefly.', { name }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const left = MAX - text.length;
  const body = (
    <div className={css.msg}>
      <h4>{T('Melding til {name} i Timefly-appen', { name })}</h4>
      <textarea
        value={text}
        maxLength={MAX}
        rows={4}
        aria-label={T('Melding')}
        onChange={(e) => setText(e.target.value)}
        data-testid="vperson-msgtext"
      />
      <span className={css.mcount} aria-live="polite">
        {T('{n} tegn igjen', { n: left })}
      </span>
      <div className={css.mchips}>
        {[T('Kan du ta en vakt fredag?'), T('Husk å stemple ut'), T('Ring meg når du kan')].map((c) => (
          <button key={c} type="button" className={css.mchip} onClick={() => setText(c)}>
            {c}
          </button>
        ))}
      </div>
      {error && (
        <p className={css.merr} role="alert">
          {error}
        </p>
      )}
      <div className={css.mfoot}>
        <Button onClick={() => change(false)}>{T('Avbryt')}</Button>
        <Button tone="pri" disabled={busy || !text.trim()} onClick={send} data-testid="vperson-msgsend">
          {busy ? T('Sender …') : T('Send')}
        </Button>
      </div>
    </div>
  );
  const trigger = (
    <button type="button" className="btn" data-testid="vperson-msg">
      <Icon name="chat" />
      {T('Melding')}
    </button>
  );
  if (phone)
    return (
      <Dialog.Root open={open} onOpenChange={change}>
        <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
        <Dialog.Portal>
          <Dialog.Overlay className="scrim" />
          <Dialog.Content className={`sheet ${css.msheet}`} aria-describedby={undefined}>
            <Dialog.Title className="sr-only">{T('Melding til {name}', { name })}</Dialog.Title>
            {body}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    );
  return (
    <Popover.Root open={open} onOpenChange={change}>
      <Popover.Trigger asChild>{trigger}</Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={css.cpop}
          sideOffset={8}
          collisionPadding={12}
          aria-label={T('Melding til {name}', { name })}
          data-testid="vperson-msgpop"
        >
          {body}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
